export interface AuditLogEntry {
  id: string;
  timestamp: string; // ISO string
  actionType: 
    | 'CREATE_BOOKING'
    | 'UPDATE_BOOKING_STATUS'
    | 'DELETE_BOOKING'
    | 'CREATE_EXPENSE'
    | 'DELETE_EXPENSE'
    | 'CREATE_INSIGHT'
    | 'DELETE_INSIGHT'
    | 'CREATE_STREAM'
    | 'DELETE_STREAM'
    | 'PRELOAD_DEMO'
    | 'CLEAR_DATA'
    | 'UPDATE_SETTINGS'
    | 'WHITELIST_USER'
    | 'REMOVE_USER'
    | 'INTRUSION_ALERT'
    | 'AES_ENCRYPTION_TOGGLE'
    | 'UNAUTHORIZED_ACCESS_BLOCKED'
    | 'INTEGRITY_VERIFICATION'
    | 'VAULT_LOCKED'
    | 'VAULT_UNLOCKED'
    | 'UPDATE_SUBSCRIPTION'
    | 'UPDATE_PAYMENT_METHOD'
    | 'PLAN_CHANGE'
    | 'SUBMIT_FEEDBACK'
    | 'DELETE_FEEDBACK'
    | 'SUBMIT_DEMO_REQUEST'
    | 'IMPORT_CSV'
    | 'RECONCILE_BATCH';
  entityType: 'Booking' | 'Expense' | 'Insight' | 'Stream' | 'System' | 'Settings' | 'Security';
  entityId?: string;
  details: string;
  performedBy?: string;
}

const AUDIT_STORAGE_KEY = 'azuraos_audit_logs';

export function getCurrentActorEmail(): string {
  try {
    const local = localStorage.getItem('solostack_local_user_email');
    if (local && local.trim() && !local.includes('iwillriseupstudio')) return local.trim();
  } catch {}
  return 'workspace-admin';
}

export const INITIAL_DEMO_LOGS: AuditLogEntry[] = [
  {
    id: 'log-init-1',
    timestamp: new Date(Date.now() - 3600000 * 24 * 2).toISOString(),
    actionType: 'PRELOAD_DEMO',
    entityType: 'System',
    details: 'Preloaded initial demo dataset with bookings, expenses, and AI strategic insights.',
    performedBy: 'demo-founder'
  },
  {
    id: 'log-init-2',
    timestamp: new Date(Date.now() - 3600000 * 18).toISOString(),
    actionType: 'CREATE_BOOKING',
    entityType: 'Booking',
    entityId: 'b-1',
    details: 'Created consultation booking for Sarah Jenkins ($150.00 ZAR). Invoice #INV-1001 generated.',
    performedBy: 'demo-founder'
  },
  {
    id: 'log-init-3',
    timestamp: new Date(Date.now() - 3600000 * 12).toISOString(),
    actionType: 'CREATE_EXPENSE',
    entityType: 'Expense',
    entityId: 'e-1',
    details: 'Logged travel expense of $85.00 ZAR linked to "retreat" stream.',
    performedBy: 'demo-founder'
  },
  {
    id: 'log-init-4',
    timestamp: new Date(Date.now() - 3600000 * 4).toISOString(),
    actionType: 'CREATE_INSIGHT',
    entityType: 'Insight',
    entityId: 'i-1',
    details: 'Created strategic insight: "Consult Pricing Optimization".',
    performedBy: 'demo-founder'
  }
];

export function loadDemoAuditLogs(): void {
  try {
    const current = getAuditLogs().filter(log => !log.id?.startsWith('log-init-'));
    const demoLogs = INITIAL_DEMO_LOGS.map(item => ({
      ...item,
      timestamp: new Date(Date.now() - (item.id === 'log-init-1' ? 86400000 : 3600000)).toISOString()
    }));
    localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify([...demoLogs, ...current]));
  } catch (e) {
    console.error('Failed to seed demo audit logs:', e);
  }
}

export function getAuditLogs(): AuditLogEntry[] {
  try {
    const saved = localStorage.getItem(AUDIT_STORAGE_KEY);
    if (saved !== null) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        // Check if demo dataset is actually loaded in DB
        let isDemoActive = false;
        try {
          const dbSaved = localStorage.getItem('solostack_db_state');
          if (dbSaved) {
            const dbObj = JSON.parse(dbSaved);
            isDemoActive = Boolean(
              dbObj.bookings?.some((b: any) => b.id === 'b-1' || b.id === 'b-2' || b.id === 'b-3') ||
              dbObj.expenses?.some((e: any) => e.id === 'e-1' || e.id === 'e-2' || e.id === 'e-3')
            );
          }
        } catch {}

        // Filter out synthetic demo logs if user is not in demo mode
        const filtered = parsed.filter((item: AuditLogEntry) => {
          if (!isDemoActive && item.id?.startsWith('log-init-')) {
            return false;
          }
          return true;
        });

        return filtered.map((item: AuditLogEntry) => ({
          ...item,
          performedBy: (!item.performedBy || item.performedBy.includes('iwillriseupstudio')) ? 'workspace-admin' : item.performedBy
        }));
      }
    }
  } catch (e) {
    console.error('Failed to parse audit logs from storage:', e);
  }
  // Fresh workspaces start with clean logs (empty array) - no unrequested demo logs
  return [];
}

export function logAuditAction(entry: Omit<AuditLogEntry, 'id' | 'timestamp'>): AuditLogEntry {
  const logs = getAuditLogs();
  const actor = (!entry.performedBy || entry.performedBy.includes('iwillriseupstudio')) 
    ? getCurrentActorEmail() 
    : entry.performedBy;

  const newEntry: AuditLogEntry = {
    id: 'log-' + Math.random().toString(36).substr(2, 9),
    timestamp: new Date().toISOString(),
    ...entry,
    performedBy: actor
  };

  const updatedLogs = [newEntry, ...logs].slice(0, 200); // keep last 200 logs
  try {
    localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify(updatedLogs));
  } catch (e) {
    console.error('Failed to persist audit log entry:', e);
  }
  return newEntry;
}

export function clearAuditLogs(): void {
  try {
    localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify([]));
  } catch (e) {
    console.error('Failed to clear audit logs:', e);
  }
}
